import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, TextField } from '@mui/material';
import { inputStyles, buttonStyles as commonButtonStyles } from './css/commonStyles';
import { ethers } from 'ethers';
import ServiceRevenueSharingABI from 'abis/ServiceRevenueSharing';
import { ERC20_ABI } from '../abis';

const RevenueSharingsTab = ({ daoOrganizerAddress, chainId, currentDao }) => {
  const [hasMetaMask, setHasMetaMask] = useState(false);
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [isCorrectChain, setIsCorrectChain] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const [claimSuccess, setClaimSuccess] = useState(false);
  const [balances, setBalances] = useState({
    usdt: '0.0000',
    kat: '0.0000',
    paxg: '0.0000',
    native: '0.0000',
  });
  const [lpInfo, setLpInfo] = useState({
    totalIssued: '0.0000',
    userHolds: '0.0000',
  });
  const [revenueInfo, setRevenueInfo] = useState({
    totalMonthly: '0.0000',
    totalDistributed: '0.0000',
    calculationTime: '0.0000',
    withdrawable: {
      usdt: '0.0000',
      kat: '0.0000',
      paxg: '0.0000',
      native: '0.0000',
    },
  });

  const [orderHandlerPct, setOrderHandlerPct] = useState('0');
  const [lpHolderPct, setLpHolderPct] = useState('0');
  const [updatingPct, setUpdatingPct] = useState(false);

  const buttonStyles = commonButtonStyles;

  useEffect(() => {
    console.log('ServiceRevenueSharingTab mounted - deploy check 2026-03-27');
  }, []);

  const loadContractData = async () => {
    if (!currentDao?.serviceRevenueSharingAddress || !window.ethereum) return;

    try {
      setIsLoading(true);
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(currentDao.serviceRevenueSharingAddress, ServiceRevenueSharingABI, signer);

      // Get wallet address
      const walletAddress = await signer.getAddress();

      // Initialize state with loading
      setBalances({
        usdt: 'loading ...',
        kat: 'loading ...',
        paxg: 'loading ...',
        native: 'loading ...',
      });
      setLpInfo({
        totalIssued: 'loading ...',
        userHolds: 'loading ...',
      });
      setRevenueInfo({
        totalMonthly: 'loading ...',
        totalDistributed: 'loading ...',
        calculationTime: 'loading ...',
        withdrawable: {
          usdt: 'loading ...',
          kat: 'loading ...',
          paxg: 'loading ...',
          native: 'loading ...',
        },
      });

      // Read current revenue percentages
      try {
        const [oh, lp] = await Promise.all([
          contract.OrderHandlerPercentage(),
          contract.LPHolderPercentage(),
        ]);
        setOrderHandlerPct(String(oh));
        setLpHolderPct(String(lp));
      } catch (error) {
        // ignore percentage load errors
      }

      // Get token addresses
      let USDTAddress, KATAddress, PAXGAddress, NativeAddress;
      try {
        USDTAddress = await contract.currencyAddresses(0);
      } catch (error) {
        USDTAddress = 'loading failed';
      }

      try {
        KATAddress = await contract.currencyAddresses(1);
      } catch (error) {
        KATAddress = 'loading failed';
      }

      try {
        PAXGAddress = await contract.currencyAddresses(2);
      } catch (error) {
        PAXGAddress = 'loading failed';
      }

      try {
        NativeAddress = await contract.currencyAddresses(3);
      } catch (error) {
        // default to address 0x0000000000000000000000000000000000000000
        NativeAddress = '0x0000000000000000000000000000000000000000';
      }

      let USDTDecimals, KATDecimals, PAXGDecimals, NativeDecimals;
      try {
        const USDTContract = new ethers.Contract(USDTAddress, ERC20_ABI, provider);
        USDTDecimals = await USDTContract.decimals();
      } catch (error) {
        console.error('Error getting USDT decimals:', error);
        USDTDecimals = 6;
      }

      try {
        const KATContractForDecimals = new ethers.Contract(KATAddress, ERC20_ABI, provider);
        KATDecimals = await KATContractForDecimals.decimals();
      } catch (error) {
        console.error('Error getting KAT decimals:', error);
        KATDecimals = 18;
      }

      try {
        const PAXGContractForDecimals = new ethers.Contract(PAXGAddress, ERC20_ABI, provider);
        PAXGDecimals = await PAXGContractForDecimals.decimals();
      } catch (error) {
        console.error('Error getting PAXG decimals:', error);
        PAXGDecimals = 18;
      }

      NativeDecimals = 18;

      // Get LP info
      let lpBalance, lPTotalSupply, lpDecimals;
      try {
        const LPContract = new ethers.Contract(currentDao.lpTokenAddress, ERC20_ABI, provider);
        lpBalance = await LPContract.balanceOf(walletAddress);
        lPTotalSupply = await LPContract.totalSupply();
        lpDecimals = await LPContract.decimals();
      } catch (error) {
        lpBalance = '0';
        lPTotalSupply = '0';
        lpDecimals = 18;
      }

      // Get revenue calculations
      let calculations,
        finalCalculation,
        totalLPShareOn1stTokenWei,
        totalRevenueIn1stTokenWei,
        totalSharesIn1stTokenWei,
        calculationTime;
      try {
        calculations = await contract.viewAllrevenueCalculations();
        if (Array.isArray(calculations) && calculations.length > 0) {
          finalCalculation = calculations[calculations.length - 1];
          totalLPShareOn1stTokenWei = finalCalculation.totalLPShareOn1stTokenWei;
          totalRevenueIn1stTokenWei = finalCalculation.totalRevenueIn1stTokenWei;
          totalSharesIn1stTokenWei = finalCalculation.totalSharesIn1stTokenWei;

          const timestamp = finalCalculation.timestamp.toNumber();
          const date = new Date(timestamp * 1000);
          calculationTime = date.toLocaleString();
        } else {
          finalCalculation = null;
          totalLPShareOn1stTokenWei = 0;
          totalRevenueIn1stTokenWei = 0;
          totalSharesIn1stTokenWei = 0;
          calculationTime = 'No data';
        }
        
      } catch (error) {
        calculations = 'loading failed';
        finalCalculation = 'loading failed';
        totalLPShareOn1stTokenWei = 'loading failed';
        totalRevenueIn1stTokenWei = 'loading failed';
        totalSharesIn1stTokenWei = 'loading failed';
        calculationTime = 'loading failed';
      }

      // Get token balances from gtTokenAddress
      let USDTBalance, KATBalance, PAXGImplementerBalance, NativeBalance;
      try {
        const USDTContract = new ethers.Contract(USDTAddress, ERC20_ABI, provider);
        USDTBalance = await USDTContract.balanceOf(currentDao.gtTokenAddress);
      } catch (error) {
        console.error('Error getting USDT balance:', error);
        USDTBalance = '0';
      }

      try {
        const KATContract = new ethers.Contract(KATAddress, ERC20_ABI, provider);
        KATBalance = await KATContract.balanceOf(currentDao.gtTokenAddress);
      } catch (error) {
        console.error('Error getting KAT balance:', error);
        KATBalance = '0';
      }

      try {
        const PAXGContract = new ethers.Contract(PAXGAddress, ERC20_ABI, provider);
        PAXGImplementerBalance = await PAXGContract.balanceOf(currentDao.gtTokenAddress);
      } catch (error) {
        console.error('Error getting PAXG balance:', error);
        PAXGImplementerBalance = '0';
      }

      try {
        NativeBalance = await provider.getBalance(currentDao.gtTokenAddress);
      } catch (error) {
        console.error('Error getting Native balance:', error);
        NativeBalance = '0';
      }

      // Get withdrawable amounts
      let withdrawableUSDT, withdrawableKAT, withdrawableinPAXG, withdrawableinNative;
      try {
        withdrawableUSDT = await contract.claimerList(walletAddress, USDTAddress);
      } catch (error) {
        withdrawableUSDT = { withdrawableAmount: '0' };
      }

      try {
        withdrawableKAT = await contract.claimerList(walletAddress, KATAddress);
      } catch (error) {
        withdrawableKAT = { withdrawableAmount: '0' };
      }

      try {
        withdrawableinPAXG = await contract.claimerList(walletAddress, PAXGAddress);
      } catch (error) {
        withdrawableinPAXG = { withdrawableAmount: '0' };
      }

      try {
        withdrawableinNative = await contract.claimerList(walletAddress, NativeAddress);
      } catch (error) {
        withdrawableinNative = { withdrawableAmount: '0' };
      }

      // Update state with calculated values
      setBalances({
        usdt: ethers.utils.formatUnits(USDTBalance || 0, USDTDecimals),
        kat: ethers.utils.formatUnits(KATBalance || 0, KATDecimals),
        paxg: ethers.utils.formatUnits(PAXGImplementerBalance || 0, PAXGDecimals),
        native: ethers.utils.formatUnits(NativeBalance || 0, NativeDecimals),
      });

      setLpInfo({
        totalIssued: Number((lPTotalSupply / Math.pow(10, lpDecimals)).toFixed(4)),
        userHolds: Number((lpBalance / Math.pow(10, lpDecimals)).toFixed(4)),
      });

      setRevenueInfo({
        totalMonthly: Number((totalRevenueIn1stTokenWei / Math.pow(10, USDTDecimals)).toFixed(4)),
        totalDistributed: Number((totalLPShareOn1stTokenWei / Math.pow(10, USDTDecimals)).toFixed(4)),
        calculationTime: calculationTime,
        withdrawable: {
          usdt: ethers.utils.formatUnits(
            withdrawableUSDT.withdrawableAmount || 0,
            USDTDecimals
          ),
          kat: ethers.utils.formatUnits(
            withdrawableKAT.withdrawableAmount || 0,
            KATDecimals
          ),
          paxg: ethers.utils.formatUnits(
            withdrawableinPAXG.withdrawableAmount || 0,
            PAXGDecimals
          ),
          native: ethers.utils.formatUnits(
            withdrawableinNative.withdrawableAmount || 0,
            NativeDecimals
          ),
        },
      });
    } catch (err) {
      console.error('Error loading contract data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePercentages = async () => {
    if (!currentDao?.serviceRevenueSharingAddress || !window.ethereum) return;
    if (!orderHandlerPct || !lpHolderPct) {
      alert('Please enter both Order Handler % and LP Holders %');
      return;
    }
    const total = Number(orderHandlerPct) + Number(lpHolderPct);
    if (Number.isNaN(total) || total > 100) {
      alert('Total percentages must be ≤ 100%.');
      return;
    }
    try {
      setUpdatingPct(true);
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(currentDao.serviceRevenueSharingAddress, ServiceRevenueSharingABI, signer);
      const gasPrice = await provider.getGasPrice();
      const increasedGasPrice = gasPrice.mul(110).div(100);
      const tx = await contract.updateRevenuePercentages(
        Number(orderHandlerPct || 0),
        Number(lpHolderPct || 0),
        { gasPrice: increasedGasPrice }
      );
      await tx.wait();
      // reload just to be sure
      await loadContractData();
      alert('Percentages updated successfully.');
    } catch (err) {
      console.error('Failed to update percentages:', err);
      alert('Failed to update percentages.');
    } finally {
      setUpdatingPct(false);
    }
  };

  useEffect(() => {
    if (isWalletConnected && currentDao?.serviceRevenueSharingAddress && isCorrectChain) {
      loadContractData();
    }
  }, [isWalletConnected, currentDao, isCorrectChain]);

  useEffect(() => {
    setHasMetaMask(typeof window.ethereum !== 'undefined');
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', checkWalletConnection);
      window.ethereum.on('chainChanged', checkWalletConnection);
      return () => {
        window.ethereum.removeListener('accountsChanged', checkWalletConnection);
        window.ethereum.removeListener('chainChanged', checkWalletConnection);
      };
    }
  }, []);

  const checkWalletConnection = async () => {
    if (!window.ethereum) {
      setHasMetaMask(false);
      setIsWalletConnected(false);
      return;
    }
    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
    setIsWalletConnected(accounts.length > 0);
    
    if (accounts.length > 0) {
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const network = await provider.getNetwork();
        const expectedChainId = parseInt(chainId || '0', 10);
        const isCorrectChain = network.chainId === expectedChainId;
        setIsCorrectChain(isCorrectChain);
      } catch (error) {
        console.error('Error checking chain:', error);
        setIsCorrectChain(false);
      }
    }
  };

  useEffect(() => {
    checkWalletConnection();
  }, []);

  const connectWallet = async () => {
    try {
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      checkWalletConnection();
    } catch (err) {
      console.error('Failed to connect wallet:', err);
    }
  };

  const switchChain = async () => {
    try {
      const expectedChainId = parseInt(chainId || '0', 10);
      
      if (isNaN(expectedChainId) || expectedChainId === 0) {
        throw new Error('Invalid chain ID');
      }
      
      const chainIdHex = '0x' + expectedChainId.toString(16);

      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: chainIdHex }],
      });
    } catch (error) {
      if (error.code === 4902) {
        try {
          const expectedChainId = parseInt(chainId || '0', 10);
          const chainIdHex = '0x' + expectedChainId.toString(16);

          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: chainIdHex,
                chainName: expectedChainId === 56 ? 'BSC' : 'Polygon',
                nativeCurrency:
                  expectedChainId === 56
                    ? {
                        name: 'BNB',
                        symbol: 'BNB',
                        decimals: 18,
                      }
                    : {
                        name: 'MATIC',
                        symbol: 'MATIC',
                        decimals: 18,
                      },
                rpcUrls: expectedChainId === 56 
                  ? ['https://bsc-dataseed.binance.org/'] 
                  : ['https://polygon-rpc.com/'],
                blockExplorerUrls: expectedChainId === 56 
                  ? ['https://bscscan.com/'] 
                  : ['https://polygonscan.com/'],
              },
            ],
          });
        } catch (addError) {
          console.error('Error adding chain:', addError);
        }
      } else {
        console.error('Error switching chain:', error);
      }
    }
  };

  const handleClaim = async () => {
    if (!currentDao?.serviceRevenueSharingAddress) {
      setClaimError('Revenue sharing contract address not found');
      return;
    }

    try {
      const hasSomethingToClaim = ['usdt', 'kat', 'paxg', 'native'].some((key) => {
        const value = revenueInfo.withdrawable[key];
        const numeric = Number(value);
        return !Number.isNaN(numeric) && numeric > 0;
      });

      if (!hasSomethingToClaim) {
        setClaimError('You currently have no withdrawable revenue to claim.');
        return;
      }
    } catch (e) {
      // If this check fails for any reason, silently continue to avoid blocking a valid claim.
    }

    try {
      setIsClaiming(true);
      setClaimError(null);
      setClaimSuccess(false);

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(currentDao.serviceRevenueSharingAddress, ServiceRevenueSharingABI, signer);

      try {
        await contract.callStatic.claim();
      } catch (staticErr) {
        let staticMessage =
          staticErr.reason ||
          staticErr.error?.reason ||
          staticErr.data?.message ||
          staticErr.message ||
          'Claim would fail due to a contract revert.';

        setClaimError(staticMessage);
        setIsClaiming(false);
        return;
      }
      const tx = await contract.claim();

      await tx.wait();
      setClaimSuccess(true);
      await loadContractData();
    } catch (err) {
      console.error('Error claiming revenue:', err?.message || err);
      let message = err.reason || err.message;
      if (err.error) {
        message = err.error.reason || err.error.message || message;
      }
      if (err.data && err.data.message) {
        message = err.data.message;
      }
      if (!message || typeof message !== 'string') {
        message = 'Transaction failed when claiming revenue. Please check that you have withdrawable balance and are on the correct network.';
      }

      setClaimError(message);
    } finally {
      setIsClaiming(false);
    }
  };

  if (!hasMetaMask) {
    return (
      <Box textAlign="center" p={3}>
        <Typography variant="body1" gutterBottom sx={{ color: '#fcfcfc' }}>
          Please install MetaMask to access this content
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#05e4bc',
            '&:hover': { backgroundColor: '#04c9a5' },
            borderRadius: '12px',
            textTransform: 'none',
            py: 1.5,
          }}
          href="https://metamask.io"
          target="_blank"
          rel="noopener noreferrer"
        >
          Install MetaMask
        </Button>
      </Box>
    );
  }

  if (!isWalletConnected) {
    return (
      <Box textAlign="center" p={3}>
        <Typography variant="body1" gutterBottom sx={{ color: '#fcfcfc' }}>
          Please connect your wallet to access this content
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#05e4bc',
            '&:hover': { backgroundColor: '#04c9a5' },
            borderRadius: '12px',
            textTransform: 'none',
            py: 1.5,
          }}
          onClick={connectWallet}
        >
          Connect Wallet
        </Button>
      </Box>
    );
  }

  if (!isCorrectChain) {
    return (
      <Box textAlign="center" p={3}>
        <Typography variant="body1" gutterBottom sx={{ color: '#fcfcfc' }}>
          Please switch to the correct network to access this content
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#05e4bc',
            '&:hover': { backgroundColor: '#04c9a5' },
            borderRadius: '12px',
            textTransform: 'none',
            py: 1.5,
            mt: 2,
          }}
          onClick={switchChain}
        >
          Switch Network
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h6" sx={{ mb: 3, pb: 2, color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        Service Revenue Sharing
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
          Token Balances
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          <Typography sx={{ color: '#fff' }}>USDT Balance: {balances.usdt}</Typography>
          <Typography sx={{ color: '#fff' }}>KAT Balance: {balances.kat}</Typography>
          <Typography sx={{ color: '#fff' }}>PAXG Balance: {balances.paxg}</Typography>
          <Typography sx={{ color: '#fff' }}>Native Token Balance: {balances.native}</Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
          LP Information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          <Typography sx={{ color: '#fff' }}>Total LP issued: {lpInfo.totalIssued}</Typography>
          <Typography sx={{ color: '#fff' }}>Number of LP tokens you hold: {lpInfo.userHolds}</Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
          Revenue Information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          <Typography sx={{ color: '#fff' }}>
            Total DAO service revenue recognized for revenue sharing in the month: {revenueInfo.totalMonthly}
          </Typography>
          <Typography sx={{ color: '#fff' }}>
            Total service revenue distributed to all DAO members: {revenueInfo.totalDistributed}
          </Typography>
          <Typography sx={{ color: '#fff' }}>
            Last calculation time: {revenueInfo.calculationTime}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
          Revenue Percentages
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          <TextField
            label="Order Handler %"
            type="number"
            value={orderHandlerPct}
            onChange={(e) => setOrderHandlerPct(e.target.value)}
            inputProps={{ min: 0, max: 100 }}
            size="small"
            fullWidth
            sx={inputStyles}
          />
          <TextField
            label="LP Holders %"
            type="number"
            value={lpHolderPct}
            onChange={(e) => setLpHolderPct(e.target.value)}
            inputProps={{ min: 0, max: 100 }}
            size="small"
            fullWidth
            sx={inputStyles}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleUpdatePercentages}
            disabled={updatingPct}
            sx={buttonStyles}
          >
            {updatingPct ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} sx={{ color: '#282f38', mr: 1 }} />
                <Typography sx={{ color: '#282f38' }}>Updating...</Typography>
              </Box>
            ) : (
              'Update'
            )}
          </Button>
          <Typography variant="caption" sx={{ color: '#aaa', mt: 1, display: 'block' }}>
            Total must be ≤ 100%
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
          Withdrawable Revenue (accumulated since previous claim)
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          <Typography sx={{ color: '#fff' }}>USDT: {revenueInfo.withdrawable.usdt}</Typography>
          <Typography sx={{ color: '#fff' }}>KAT: {revenueInfo.withdrawable.kat}</Typography>
          <Typography sx={{ color: '#fff' }}>PAXG: {revenueInfo.withdrawable.paxg}</Typography>
          <Typography sx={{ color: '#fff' }}>Native Token: {revenueInfo.withdrawable.native}</Typography>
        </Box>
      </Box>

      {claimError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {claimError}
        </Typography>
      )}

      {claimSuccess && (
        <Typography color="success" sx={{ mb: 2 }}>
          Revenue claimed successfully!
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
        <Button
          variant="contained"
          onClick={loadContractData}
          disabled={isLoading}
          sx={buttonStyles}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={20} sx={{ color: '#FFFFFF', mr: 1 }} />
              <Typography sx={{ color: '#FFFFFF' }}>Loading...</Typography>
            </Box>
          ) : (
            'Load Data'
          )}
        </Button>

        <Button
          variant="contained"
          onClick={handleClaim}
          disabled={isClaiming}
          sx={buttonStyles}
        >
          {isClaiming ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={20} sx={{ color: '#282f38', mr: 1 }} />
              <Typography sx={{ color: '#282f38' }}>Claiming...</Typography>
            </Box>
          ) : (
            'Claim Revenue'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default RevenueSharingsTab;
